import React, { useMemo } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import classNames from 'classnames';

import { useConfiguration } from '../../../../context/configurationContext';
import { useIntl } from '../../../../util/reactIntl';
import { parse, stringify } from '../../../../util/urlHelpers';

import { makeGetListingsByIdSelector } from '../../../../ducks/marketplaceData.duck';

import { AspectRatioWrapper, NamedLink } from '../../../../components';

import { buildStripCategoryParams, stripParamKey } from '../../landingStrips';

import ChipFilter from './ChipFilter/ChipFilter';
import ListingCarousel from './ListingCarousel';

import css from './SectionListingStrip.module.css';

const NUM_PLACEHOLDERS = 6;

// Stable empty-array reference so useSelector's referential equality check
// returns the same reference across renders when the strip has no listings
// — avoids the "selector returned different result" warning + render loop.
const EMPTY_LISTINGS = [];

/**
 * Reusable horizontal product strip for the custom landing page.
 *
 * Chip selection lives entirely in the URL (`?strip-<sectionId>=<chipKey>`).
 * Each chip is a NamedLink with a `to.search` that adds/removes its own
 * key — clicking it navigates back to LandingPage with the new search,
 * which re-runs `LandingPage.loadData` and refetches the strip. This
 * means deep links, browser back/forward, and SSR all share the same
 * source of truth: the URL.
 *
 * @param {Object} props
 * @param {string} props.sectionId – stable id matching the duck slice key
 * @param {string} props.titleMessageId – translation key for the section title
 * @param {string[]} [props.parentCategoryPath] – path of category ids to lock
 *   the strip to. The last entry is the node whose subcategories become the
 *   chip list.
 * @param {boolean} [props.hideChips=false] – suppress the chip row entirely
 *   (the strip still applies the parent-path filter on its listings).
 * @param {boolean} [props.lightBg=false] – render the section with a white
 *   edge-to-edge background, used to alternate strips visually on the page.
 * @returns {JSX.Element}
 */
const SectionListingStrip = props => {
  const {
    sectionId,
    titleMessageId,
    parentCategoryPath = [],
    hideChips = false,
    lightBg = false,
  } = props;

  const intl = useIntl();
  const config = useConfiguration();
  const location = useLocation();

  // Read the selected chip from the URL. `null` = the "All" chip.
  const queryParams = parse(location.search);
  const paramKey = stripParamKey(sectionId);
  const selectedKey = queryParams[paramKey] || null;

  const stripState = useSelector(state => state.LandingPage?.strips?.[sectionId]);
  const listingIds = stripState?.listingIds;
  // Per-instance memoized selector — reselect caches the denormalised
  // array against [entities, listingIds] so the selector body doesn't
  // re-run on unrelated dispatches and the returned reference stays
  // identical across renders. Pattern mirrors SearchPageWithGrid /
  // SearchPageWithMap.
  const selectListingsById = useMemo(makeGetListingsByIdSelector, []);
  const listings = useSelector(
    state => (listingIds?.length ? selectListingsById(state, listingIds) : EMPTY_LISTINGS),
    shallowEqual
  );

  // Resolve the chip source. For parent-scoped strips, walk the tree to the
  // deepest node and surface its subcategories. For top-level strips, the
  // root categories are the chips.
  const allCategories = config?.categoryConfiguration?.categories || [];
  const parentNode = parentCategoryPath.reduce(
    (acc, id) => (acc?.subcategories || []).find(c => c.id === id) || null,
    { subcategories: allCategories }
  );
  const sourceCategories = (parentCategoryPath.length
    ? parentNode?.subcategories || []
    : allCategories
  ).filter(c => !c.hidden);

  // Build a `to.search` for each chip that flips just this strip's
  // param while preserving every other URL parameter (including other
  // strips' selections).
  const buildChipTo = key => {
    const next = { ...queryParams };
    if (key) {
      next[paramKey] = key;
    } else {
      delete next[paramKey];
    }
    const search = stringify(next);
    return { search: search ? `?${search}` : '' };
  };

  const chips = [
    {
      key: null,
      label: intl.formatMessage({ id: 'SectionListingStrip.chipAll' }),
      to: buildChipTo(null),
    },
    ...sourceCategories.map(c => ({
      key: c.id,
      label: c.name,
      to: buildChipTo(c.id),
    })),
  ];

  const inProgress = stripState?.inProgress;
  const error = stripState?.error;
  // A strip that hasn't been fetched yet (client-side navigation before
  // loadData lands) reads as loading, not as empty.
  const showPlaceholders = (inProgress || !stripState) && listings.length === 0;

  // Nothing to show and nothing selected: the section goes away entirely
  // rather than leaving a header over an empty row. With a chip selected
  // the section stays, message and all, so the chips remain reachable
  // and the visitor can widen the filter again.
  const hasLoaded = !!stripState && !inProgress && !error;
  if (hasLoaded && listings.length === 0 && !selectedKey) {
    return null;
  }

  // "Vis alle →" preserves both the parent filter and the selected chip so
  // the SearchPage opens pre-filtered to the user's current view.
  const viewAllParams = buildStripCategoryParams(parentCategoryPath, selectedKey);
  const viewAllTo = Object.keys(viewAllParams).length
    ? { search: '?' + new URLSearchParams(viewAllParams).toString() }
    : undefined;

  return (
    <section className={classNames(css.root, { [css.lightBg]: lightBg })}>
      <div className={css.inner}>
        <div className={css.header}>
          <h3 className={css.title}>{intl.formatMessage({ id: titleMessageId })}</h3>
          <NamedLink className={css.viewAll} name="SearchPage" to={viewAllTo}>
            {intl.formatMessage({ id: 'SectionListingStrip.viewAll' })}
          </NamedLink>
        </div>

        {hideChips ? null : <ChipFilter chips={chips} selectedKey={selectedKey} />}

        {error && listings.length === 0 ? (
          <div className={css.empty}>
            {intl.formatMessage({ id: 'SectionListingStrip.errorMessage' })}
          </div>
        ) : showPlaceholders ? (
          <div className={css.scroller}>
            {Array.from({ length: NUM_PLACEHOLDERS }).map((_, i) => (
              <div key={i} className={css.cardWrapper}>
                <AspectRatioWrapper
                  className={css.placeholder}
                  width={1}
                  height={1}
                  aria-hidden="true"
                />
              </div>
            ))}
          </div>
        ) : listings.length === 0 ? (
          <div className={css.empty}>
            {intl.formatMessage({ id: 'SectionListingStrip.emptyMessage' })}
          </div>
        ) : (
          <ListingCarousel listings={listings} />
        )}
      </div>
    </section>
  );
};

export default SectionListingStrip;
